<template lang="pug">
.view-part.flex
  .view-table.customScrollbar(ref="table")
    .header.flex.column.align-start(:style="widthStyle")
      .tr.flex.align-stretch(:style="widthStyle")
        .th.time &nbsp;
        el-tooltip(v-if="showProSlot", placement="bottom")
          .pro-slot-legend(slot="content")
            div(v-if="availabilityText") {{ availabilityText }}
            div 專業人士當天狀況：綠色為可排案時段、灰色為不可排案、紅色為已有預約或活動
          .th.pro-slot.flex.align-center.justify-center
            i.el-icon-user
        .th.room.flex.align-center(
          v-for="room in rooms", :key="room.id")
          span {{room.name}}
    .body.flex.column.align-start(:style="widthStyle")
      template(v-for="hour in hours")
        .tr.flex.align-stretch(
          :key="`up-${hour.value}`", :data-hour="hour.value", :style="widthStyle")
          .td.time.flex.align-center {{ hour.text }}
          .td.pro-slot(v-if="showProSlot")
          .td.room(v-for="room in rooms", :key="room.id")
        .tr.flex.align-stretch(:key="`down-${hour.value}`", :style="widthStyle")
          .td.time
          .td.pro-slot(v-if="showProSlot")
          .td.room(v-for="room in rooms", :key="room.id")
    template(v-for="block in proSlotBlocks")
      el-tooltip(:key="block.key", :content="block.text", placement="right", :open-delay="200")
        .pro-slot-block(:class="block.type", :style="block.style")
    template(v-for="activity in activities", v-if="rooms.length > 0")
      activity-block(
        :key="activity.id"
        v-if="activity.status !== 'cancelled' && activity.location_type === 'inhouse'"
        :editable="activity.end_at > editBoundary"
        :offset="roomOffset"
        :rooms="rooms", :tags="tags", :activity="activity")
    template(v-for="appointment in reserveList", v-if="rooms.length > 0")
      appointment-block(
        :key="appointment.id"
        v-if="appointment.status !== 'cancelled'"
        @click="showDetail(appointment)"
        :editable="appointment.end_at > editBoundary"
        :offset="roomOffset"
        :rooms="rooms", :tags="tags", :appointment="appointment")
  el-dialog(
    width="600px"
    title="預約詳細記錄"
    :visible.sync="detailVisible"
    @closed="clearDetail"
  )
    appointment-detail(
      v-if="detailAppointment !== undefined"
      @edit="execCommand('edit', $event)"
      @recreate="execCommand('recreate', $event)"
      @cancel="execCommand('cancel', $event)"
      :tags="tags"
      :data="detailAppointment"
      :orgMap="projectOrgMap")
  appointment-edit(
    ref="appointmentEdit"
    @finish="reloadDataWithDelay"
  )
  appointment-creator(
    ref="appointmentCreator"
    @finish="reloadDataWithDelay"
  )
</template>

<script>
import { getTags } from '@/api/tags';
import { getBranches } from '@/api/place';
import { getAllAppointments, cancelAppointment, getAppointment } from '@/api/reserve';
import { getActivities } from '@/api/activity';
import { getOrganizations } from '@/api/project';
import { getProfessionalCalendar } from '@/api/professionals';
import AppointmentEditor from '@/components/drawers/AppointmentEdit.vue';
import AppointmentCreate from '@/components/drawers/AppointmentCreate.vue';
import AppointmentBlock from './AppointmentBlock.vue';
import ActivityBlock from './ActivityBlock.vue';
import AppointmentDetail from './AppointmentDetail.vue';
import {
  buildDayInfo, describeDay, hasWeeklySlots, minuteToTime, reasonText,
} from '@/util/availability';

const TIME_WIDTH = 80;
const PRO_SLOT_WIDTH = 24;
// 窄欄的疊放順序，後面的蓋在前面上方
const PRO_SLOT_LAYERS = ['available', 'off', 'busy'];

/** 從區間（分鐘）扣掉 cutters 覆蓋的部分，回傳剩下的片段 */
function subtractRanges(range, cutters) {
  let pieces = [{ start: range.start, end: range.end }];
  cutters.forEach((c) => {
    pieces = pieces.flatMap((p) => {
      if (c.end <= p.start || p.end <= c.start) {
        return [p];
      }
      return [{ start: p.start, end: c.start }, { start: c.end, end: p.end }].filter((x) => x.end > x.start);
    });
  });
  return pieces;
}

export default {
  name: 'appointment-room-view',
  components: {
    'activity-block': ActivityBlock,
    'appointment-block': AppointmentBlock,
    'appointment-detail': AppointmentDetail,
    'appointment-edit': AppointmentEditor,
    'appointment-creator': AppointmentCreate,
  },
  props: {
    branch: {
      type: Number,
      required: true,
    },
    time: {
      type: Date,
      required: true,
    },
    public: {
      type: Boolean,
      default: false,
    },
    hour: {
      type: Number,
      default: 9,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    /** 專業人士 id；有值時在時間欄與空間欄之間多一條窄欄，標示該專業人士當天的可排案時段與已有的預約／活動 */
    professional: {
      type: Number,
      default: undefined,
    },
    /** 窄欄不標示為「已有預約」的預約 id（編輯中的那一筆） */
    excludeAppointment: {
      type: Number,
      default: undefined,
    },
    /** 專業人士可排案時段設定（後端 availability）；null 時窄欄只標示已有的預約／活動 */
    availability: {
      type: Object,
      default: null,
    },
    holidays: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const hours = [];
    for (let i = 0; i < 24; i += 1) {
      hours.push({
        text: `${i % 12 === 0 ? 12 : i % 12} ${i < 12 ? 'AM' : 'PM'}`,
        value: i,
      });
    }
    return {
      reserveList: [],
      activities: [],
      tags: [],
      branchMap: {},

      filter: {},

      detailUser: undefined,
      hours,

      detailVisible: false,
      detailAppointment: undefined,
      editBoundary: parseInt((new Date()).getTime() / 1000, 10) - 86400,
      projectOrgMap: {},
      /** 專業人士當天（跨館別）的預約與活動，{ start, end, text }，start / end 為當天分鐘數 */
      proBusy: [],
    };
  },
  computed: {
    rooms() {
      return this.branchMap[this.branch]?.rooms || [];
    },
    tableWidth() {
      return 160 * this.rooms.length;
    },
    showProSlot() {
      return this.professional !== undefined;
    },
    roomOffset() {
      return TIME_WIDTH + (this.showProSlot ? PRO_SLOT_WIDTH : 0);
    },
    dayStart() {
      const date = new Date(this.time);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dayInfo() {
      return buildDayInfo([this.dayStart], this.availability, this.holidays)[0];
    },
    availabilityText() {
      if (!this.availability) {
        return '';
      }
      return `專業人士${describeDay(this.dayInfo, hasWeeklySlots(this.availability))}`;
    },
    /** 窄欄的區塊，座標與 AppointmentBlock 一致（每分鐘 2px、表頭 60px），依 PRO_SLOT_LAYERS 疊放：
     * 綠底為可排案時段（只算未指定館別或包含本館別的時段；未設定每週時段時不畫）、
     * 灰底為不可排案、紅底為已有的預約／活動 */
    proSlotBlocks() {
      if (!this.showProSlot) {
        return [];
      }
      const range = (r) => `${minuteToTime(r.start)}–${minuteToTime(r.end)}`;
      const info = this.dayInfo;
      const ranges = [];
      if (this.availability) {
        if (info.isOff) {
          const text = info.isUnavailable
            ? `不可排案${info.reason ? `：${info.reason}` : ''}`
            : `國定假日：${info.holidayName}`;
          ranges.push({
            type: 'off', start: 0, end: 1440, text,
          });
        } else {
          info.branchSlots
            .filter((s) => s.branchIds.length === 0 || s.branchIds.includes(this.branch))
            .forEach((s) => ranges.push({
              type: 'available', start: s.start, end: s.end, text: `可排案時段 ${range(s)}`,
            }));
          info.partialOff.forEach((p) => ranges.push({
            type: 'off', start: p.start, end: p.end, text: `不可排案 ${range(p)}（${reasonText(p.reason)}）`,
          }));
        }
      }
      this.proBusy.forEach((b) => ranges.push({
        type: 'busy', start: b.start, end: b.end, text: `${b.text} ${range(b)}`,
      }));
      // 被上層蓋住的部分切掉，讓每段各自成為一條圓角色條
      const layer = (r) => PRO_SLOT_LAYERS.indexOf(r.type);
      return ranges.flatMap((r, i) => subtractRanges(r, ranges.filter((c) => layer(c) > layer(r)))
        .map((p) => ({
          key: `${r.type}-${i}-${p.start}`,
          type: r.type,
          text: r.text,
          style: {
            left: `${TIME_WIDTH}px`,
            top: `${60 + p.start * 2}px`,
            height: `${(p.end - p.start) * 2}px`,
          },
        })));
    },
    widthStyle() {
      return {
        width: `${this.tableWidth + this.roomOffset - TIME_WIDTH}px`,
      };
    },
  },
  watch: {
    branch() {
      this.changeBranch();
    },
    time() {
      this.changeDate();
      this.loadProBusy();
    },
    professional() {
      this.loadProBusy();
    },
    excludeAppointment() {
      this.loadProBusy();
    },
  },
  methods: {
    reloadDataWithDelay() {
      setTimeout(() => {
        this.loadData();
      }, 100);
    },
    execCommand(cmd, data) {
      if (cmd === 'edit') {
        this.detailVisible = false;
        this.$refs.appointmentEdit.$emit('show', data);
      } else if (cmd === 'cancel') {
        this.detailVisible = false;
        this.cancelAppointment(data);
      } else if (cmd === 'recreate') {
        this.detailVisible = false;
        this.$refs.appointmentCreator.$emit('show', data);
      }
    },
    async cancelAppointment(appointment) {
      this.$confirm('確定要取消此預約嗎').then(
        () => this.$execWithLoading(async () => {
          await cancelAppointment(appointment.id);
          this.$showSuccess('已取消預約');
          this.loadData();
        }, (e) => this.$showAxiosException('取消預約失敗', e)),
      ).catch(() => {});
    },
    getFilter() {
      const start = new Date(this.time);
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setHours(23, 59, 59, 999);
      const filter = {
        start_at: parseInt(start.getTime() / 1000, 10),
        end_at: parseInt(end.getTime() / 1000, 10),
        branch: (this.branch !== '' && this.branch) ? this.branch : undefined,
      };
      return filter;
    },
    changeDate() {
      this.page = 1;
      this.$execWithLoading(async () => {
        await this.loadReserveList();
        await this.loadActivities();
      }, (e) => this.$showAxiosException('讀取空間預約失敗', e));
    },
    loadData() {
      this.editBoundary = parseInt((new Date()).getTime() / 1000, 10) - 86400;

      this.reserveList = [];
      this.activities = [];
      this.$execWithLoading(async () => {
        await Promise.all([
          this.loadBranch(),
          this.loadReserveList(),
          this.loadTags(),
          this.loadActivities(),
          this.loadOrganizations(),
          this.loadProBusy(),
        ]);

        this.scrollToHour(this.hour);
      }, (e) => this.$showAxiosException('讀取空間預約失敗', e));
    },
    /** 載入專業人士當天前後的預約與活動（含跨午夜），裁成當天的分鐘區間；失敗時不標示 */
    async loadProBusy() {
      const { professional, excludeAppointment } = this;
      const dayStart = this.dayStart.getTime() / 1000;
      this.proBusy = [];
      if (professional === undefined) {
        return;
      }
      let data;
      try {
        data = await getProfessionalCalendar(professional, dayStart - 86400, dayStart + 86400 * 2 - 1);
      } catch (e) {
        return;
      }
      if (professional !== this.professional || excludeAppointment !== this.excludeAppointment
        || dayStart !== this.dayStart.getTime() / 1000) {
        return;
      }
      const place = (room) => (room ? `（${[room.branch?.name, room.name].filter((n) => !!n).join(' ')}）` : '');
      const items = [
        ...(data.appointments || [])
          .filter((a) => a.id !== excludeAppointment)
          .map((a) => ({ ...a, text: `已有預約${place(a.room)}` })),
        ...(data.occupied || []).map((a) => ({ ...a, text: '已有預約（其他館別）' })),
        ...(data.activities || []).map((a) => ({ ...a, text: `已有活動：${a.name}${place(a.room)}` })),
      ];
      this.proBusy = items
        .map((item) => ({
          start: Math.max(0, Math.round((item.start_at - dayStart) / 60)),
          end: Math.min(1440, Math.round((item.end_at - dayStart) / 60)),
          text: item.text,
        }))
        .filter((item) => item.end > item.start);
    },
    async loadOrganizations() {
      const data = await getOrganizations();
      const projectOrgMap = {};
      data.forEach((d) => {
        d.projects.forEach((p) => {
          projectOrgMap[p.id] = d;
        });
      });
      this.projectOrgMap = projectOrgMap;
    },
    async loadActivities() {
      const rsp = await getActivities(this.getFilter());
      let activities = rsp;
      if (rsp.activities) {
        activities = rsp.activities;
      }
      this.activities = JSON.parse(JSON.stringify(activities));
    },
    async loadReserveList() {
      const reserveList = await getAllAppointments(this.getFilter());
      this.reserveList = reserveList.map((r) => ({
        ...r,
        type: r.appointment_type || r.type || '',
        subtype: r.appointment_subtype || r.subtype || 'statutoryagents',
      }));
    },
    async loadTags() {
      const tags = await getTags();
      this.tags = tags;
    },
    async loadBranch() {
      const branches = await getBranches(this.public || undefined);
      const branchMap = {};
      branches.forEach((b) => {
        branchMap[b.id] = b;
      });
      this.branchMap = branchMap;
      return branches[0];
    },
    changeBranch() {
      this.loadData();
    },
    scrollToHour(hour) {
      this.$refs.table.scrollTop = 60 * 2 * hour;
    },
    showDetail(current) {
      if (!this.clickable) {
        return;
      }
      // 先清掉上一筆，避免載入失敗時殘留舊資料
      this.detailAppointment = undefined;
      this.$execWithLoading(async () => {
        this.detailAppointment = await getAppointment(current.id);
        this.detailVisible = true;
      }, (e) => {
        this.$showAxiosException('載入資料失敗', e);
      });
    },
    // 視窗關閉（含動畫結束）後清空細節資料
    clearDetail() {
      this.detailAppointment = undefined;
    },
  },
  mounted() {
    setTimeout(() => {
      this.loadData();
    }, 10);
    this.$on('reload', () => {
      this.loadData();
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';
$pro-slot-available-color: #C6E7D3;
$pro-slot-available-border-color: #8FCBA7;
$pro-slot-busy-color: #F3B7B2;
$pro-slot-busy-border-color: #E0857E;

.view-part {
  // 專業人士可排案時段窄欄，寬度需與 script 的 PRO_SLOT_WIDTH 一致
  .pro-slot {
    flex: 0 0 24px;
    padding: 0;
    color: $text-secondary-color;
    cursor: default;
  }
  // 色塊本身占滿窄欄寬度（hover 範圍），內縮的圓角色條畫在 ::before
  .pro-slot-block {
    position: absolute;
    width: 24px;
    &::before {
      content: '';
      position: absolute;
      inset: 1px 5px;
      border-radius: 3px;
      box-sizing: border-box;
    }
    &.available::before {
      background: $pro-slot-available-color;
      border: 1px solid $pro-slot-available-border-color;
    }
    &.off::before {
      background: repeating-linear-gradient(-45deg, #EEF0F3 0 4px, #E1E4E9 4px 8px);
      border: 1px solid $border-light-color;
    }
    &.busy::before {
      background: $pro-slot-busy-color;
      border: 1px solid $pro-slot-busy-border-color;
    }
  }
  flex: 1;
  background: white;
  margin: 30px;
  margin-bottom: 0;
  overflow: hidden;
  position: relative;
  .view-table {
    flex: 1;
    border: 1px solid $border-base-color;
    overflow: auto;
    overflow: scroll;
    position: relative;
  }
  .tr {
    flex: 0 60px;
  }
  .header {
    position: sticky;
    top: 0;
    .tr {
      background: #E6F4F4;
      .th {
        background: #E6F4F4;
        border-right: 1px solid $border-base-color;
        &:first-child {
          position: sticky;
          left: 0;
        }
      }
    }
    z-index: 2;
  }
  .body {
    .tr {
      .td {
        border-right: 1px solid $border-base-color;
        border-top: 1px solid $border-info-color;
        background: white;
        &:first-child {
          position: sticky;
          left: 0;
          z-index: 1;
        }
      }
      &:nth-child(2n) {
        .td {
          border-top: 1px solid $border-base-color;
        }
        .td:first-child {
          border-top: none;
        }
      }
    }
  }
  .time {
    flex: 0 0 80px;
    padding: 0 16px;
  }
  .room {
    flex: 0 0 160px;
    padding: 0 16px;
  }
}
</style>
